import type { AuthError, Session } from '@supabase/supabase-js'
import type React from 'react'
import { createContext, useContext, useEffect, useState } from 'react'
import { type AuthUser, supabase } from '../lib/supabase'

interface AuthContextType {
  user: AuthUser | null
  session: Session | null
  loading: boolean
  isPasswordRecovery: boolean
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error?: AuthError | null }>
  signIn: (email: string, password: string) => Promise<{ error?: AuthError | null }>
  signOut: () => Promise<{ error?: AuthError | null }>
  resetPassword: (email: string) => Promise<{ error?: AuthError | null }>
  changePassword: (password: string) => Promise<{ error?: AuthError | null }>
  clearPasswordRecovery: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false)

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        console.log('[AuthContext] Password recovery')
        setIsPasswordRecovery(true)
        setSession(session)
        setLoading(false)
        // Don't set user during password recovery
        setUser(null)
        return
      }

      if (event === 'SIGNED_IN') {
        setSession(session)
        if (session?.user) {
          console.log('[AuthContext] Signed in')
          setUser({
            id: session.user.id,
            email: session.user.email!,
          })
        } else {
          console.log('User not found')
        }
        setLoading(false)
        return
      }

      if (event === 'SIGNED_OUT') {
        console.log('[AuthContext] Sign out')
        setUser(null)
        setSession(null)
        setIsPasswordRecovery(false)
        setLoading(false)
        return
      }

      if (event === 'TOKEN_REFRESHED') {
        console.log('[AuthContext] Token refreshed')
        setSession(session)
        return
      }

      if (event === 'INITIAL_SESSION') {
        console.log('[AuthContext] Initial session')
        setSession(session)
        if (session?.user) {
          console.log('[AuthContext] Initial session with user')
          setUser({
            id: session.user.id,
            email: session.user.email!,
          })
        } else {
          console.log('[AuthContext] Initial session without user')
          setUser(null)
        }
        setLoading(false)
        // Don't clear password recovery state on initial session
        return
      }

      // Default case - just update session
      setSession(session)
    })
    return () => subscription.unsubscribe()
  }, [])

  const signUp = async (email: string, password: string, fullName?: string) => {
    // Split full name into first and last names
    const nameParts = fullName?.split(' ') || []
    const firstName = nameParts[0] || ''
    const lastName = nameParts.slice(1).join(' ') || ''

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          first_name: firstName,
          last_name: lastName,
        },
      },
    })

    // Note: The profile will be created automatically by the database trigger
    // No need to manually create it here since handle_new_user() trigger does this

    return { error }
  }

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { error }
  }

  const signOut = async () => {
    try {
      console.log('Starting signOut process...')

      // Clear local state immediately for better UX
      setUser(null)
      setSession(null)

      // Try to sign out from Supabase with shorter timeout
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('SignOut timeout')), 1000)
      )

      const signOutPromise = supabase.auth.signOut()

      try {
        const result = await Promise.race([signOutPromise, timeoutPromise])
        const { error } = result
        console.log('SignOut completed with result:', { error })
        return { error }
      } catch (timeoutError) {
        console.warn('SignOut timed out, but local state cleared:', timeoutError)
        // Local state is already cleared, so user experience is good
        return { error: null }
      }
    } catch (error) {
      console.error('SignOut error:', error)
      return { error: error as any }
    }
  }

  const resetPassword = async (email: string) => {
    const appUrl = process.env.REACT_APP_URL || window.location.origin
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${appUrl}/reset-password`,
    })
    return { error }
  }

  const changePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({
      password: password,
    })

    if (!error) {
      // Clear password recovery state after successful password change
      setIsPasswordRecovery(false)
    }

    return { error }
  }

  const clearPasswordRecovery = () => {
    setIsPasswordRecovery(false)
  }

  const value: AuthContextType = {
    user,
    session,
    loading,
    isPasswordRecovery,
    signUp,
    signIn,
    signOut,
    resetPassword,
    changePassword,
    clearPasswordRecovery,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
